<script lang="ts" setup>
import { z } from 'zod';
import {
  reactive,
  ref,
  showError,
  useAuth,
  useI18n,
  useSupabaseClient,
  useToast,
  useAsyncData,
  useMetrics,
} from '#imports';

const emit = defineEmits(['toggle-auth-step', 'start-onboarding']);
const { t } = useI18n();

const supabase = useSupabaseClient();
const toast = useToast();

// Signup form
const signupModel = reactive({
  email: '',
  password: '',
  confirmPassword: '',
  privacyPolicyAccepted: false,
});

const signupSchema = z.object({
  email: z
    .string({ message: 'Email is required' })
    .email({ message: 'Please enter a valid email address' }),
  password: z
    .string({ message: 'Password is required' })
    .min(8, { message: 'Password must be at least 8 characters long' })
    .max(255, { message: 'Password must be at most 255 characters long' }),
  confirmPassword: z
    .string({ message: 'Confirm password is required' })
    .refine((value) => value === signupModel.password, {
      message: 'Passwords must match',
    }),
  privacyPolicyAccepted: z
    .boolean({ message: 'Privacy policy is required' })
    .refine((val) => val === true, {
      message: 'You must accept the privacy policy to continue',
    }),
});

const { signUp, currentUser, fetchCurrentUser } = useAuth();
const { trackEvent } = useMetrics();
const isLoading = ref(false);
const isSent = ref(false);
const signupFailed = ref(false);
const { data: userIPAddress } = await useAsyncData(() =>
  $fetch('https://api.ipify.org?format=json'),
);
const handleSignup = async () => {
  const validationResult = signupSchema.safeParse(signupModel);
  if (!validationResult.success) return;
  isLoading.value = true;

  try {
    await signUp(signupModel.email, signupModel.password);
    await fetchCurrentUser();

    if (currentUser.value) {
      const { error } = await supabase
        .from('privacy_policy_agreement_audit_log')
        .insert({
          user_id: currentUser.value.profile.id,
          terms_version: '1.0',
          ip: userIPAddress.value.ip || 'Could not get IP address',
        });

      if (error) {
        showError('Failed to log privacy policy agreement:', error);
      }

      emit('start-onboarding');
      isSent.value = true;
      signupFailed.value = false;
      trackEvent('joined')
    } else {
      signupFailed.value = true;
    }
  } catch (error) {
    showError('Signup error:', error);
    signupFailed.value = true;
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <div>
    <MPMessage v-if="signupFailed" version="error">
      {{ t('auth.signup.signupFailed') }}
    </MPMessage>
    <!-- <p v-if="isSent" class="signup-success">
      Check your email for a link to confirm your account
    </p> -->
    <form v-if="!isSent" class="signup" @submit.prevent="handleSignup">
      <MPInput
        v-model="signupModel.email"
        name="email"
        :label="t('auth.login.email')"
        type="email"
        :placeholder="t('auth.login.email')"
        class="input-email"
        :validation="signupSchema.shape.email"
        hide-label
        required
      />
      <MPInput
        v-model="signupModel.password"
        name="password"
        type="password"
        :label="t('auth.login.password')"
        :placeholder="t('auth.login.password')"
        :validation="signupSchema.shape.password"
        toggle-mask
        hide-label
        required
        autocomplete
      />
      <MPInput
        v-model="signupModel.confirmPassword"
        name="confirmPassword"
        type="password"
        :label="t('auth.login.confirmPassword')"
        :placeholder="t('auth.login.confirmPassword')"
        :validation="signupSchema.shape.confirmPassword"
        toggle-mask
        hide-label
        required
        autocomplete
      />
      <MPCheckbox
        v-model="signupModel.privacyPolicyAccepted"
        name="privacyPolicyAccepted"
        label="I agree to the"
        link="/info/privacy-policy"
        link-text="Privacy Policy"
        :validation="signupSchema.shape.privacyPolicyAccepted"
        required
      />
      <MPButton
        :label="t('auth.signup.title')"
        type="submit"
        :disabled="isLoading"
        :is-loading="isLoading"
      />
      <MPButton
        label="Login"
        version="secondary"
        @click="emit('toggle-auth-step')"
      />
    </form>
    <p class="auth-footer">
      <small>
        {{ t('auth.signup.agreeToTerms') }}
      </small>
    </p>
  </div>
</template>

<style lang="postcss" scoped>
.signup {
  display: grid;
  gap: 1rem;
}
a {
  cursor: pointer;
  text-align: center;
}
.auth-footer {
  color: var(--color-slate-400);
}
</style>
